import { Fighter, createFighter } from '../types/fighter';

// Интерфейс для информации о составе врагов на глубине
export interface DepthEnemyInfo {
  depth: number;
  enemyCount: number;
  composition: {
    elite: number;      // Количество элитных врагов
    support: number;    // Количество врагов поддержки
    crowd: number;      // Количество массовых врагов
  };
  possibleEnemies: Fighter[]; // Все возможные типы врагов на этой глубине
}

// Функции для создания типов врагов (без случайности, для предпросмотра)
const createCrowdEnemyPreview = (name: 'Костерез' | 'Костяной убийца'): Fighter => {
  if (name === 'Костяной убийца') {
    return createFighter('preview-crowd-killer', 'Костяной убийца', 0, 0, false, 10, 13);
  } else {
    return createFighter('preview-crowd', 'Костерез', 0, 0, false, 10);
  }
};

const createSupportEnemyPreview = (name: 'Топор-щитовик' | 'Мутант-кинжальщик' | 'Топорщик' | 'Костяной топорщик' | 'Метатель'): Fighter => {
  switch (name) {
    case 'Топор-щитовик':
      return createFighter('preview-support-axe-shield', 'Топор-щитовик', 0, 0, false, 11, 14);
    case 'Мутант-кинжальщик':
      return createFighter('preview-support-mutant', 'Мутант-кинжальщик', 0, 0, false, 10, undefined, 18);
    case 'Топорщик':
      return createFighter('preview-support-axe', 'Топорщик', 0, 0, false, 11); // Только топор, без доспехов
    case 'Костяной топорщик':
      return createFighter('preview-support-bone-axe', 'Костяной топорщик', 0, 0, false, 11, undefined, 19);
    case 'Метатель':
      return createFighter('preview-support-thrower', 'Метатель', 0, 0, false, 16);
  }
};

const createEliteEnemyPreview = (name: 'Каменный разрушитель' | 'Копейщик'): Fighter => {
  if (name === 'Копейщик') {
    return createFighter('preview-elite-spear', 'Копейщик', 0, 0, false, 17);
  } else {
    return createFighter('preview-elite-destroyer', 'Каменный разрушитель', 0, 0, false, 12, 15);
  }
};

// Получаем информацию о врагах для глубины
export const getDepthEnemyInfo = (depth: number): DepthEnemyInfo => {
  let composition = { elite: 0, support: 0, crowd: 0 };
  const possibleEnemies: Fighter[] = [];

  switch (depth) {
    case 1:
      composition = { elite: 0, support: 0, crowd: 1 };
      // Возможные враги: Костерез, Костяной убийца
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
      break;

    case 2:
      composition = { elite: 0, support: 1, crowd: 2 };
      // Возможные враги поддержки: Топор-щитовик, Мутант-кинжальщик, Топорщик, Метатель
      possibleEnemies.push(createSupportEnemyPreview('Топор-щитовик'));
      possibleEnemies.push(createSupportEnemyPreview('Мутант-кинжальщик'));
      possibleEnemies.push(createSupportEnemyPreview('Топорщик'));
      possibleEnemies.push(createSupportEnemyPreview('Метатель'));
      // Возможные враги массовки: Костерез, Костяной убийца
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
      break;

    case 3:
      composition = { elite: 0, support: 2, crowd: 3 };
      // Возможные враги поддержки: Топор-щитовик, Мутант-кинжальщик, Топорщик, Метатель
      possibleEnemies.push(createSupportEnemyPreview('Топор-щитовик'));
      possibleEnemies.push(createSupportEnemyPreview('Мутант-кинжальщик'));
      possibleEnemies.push(createSupportEnemyPreview('Топорщик'));
      possibleEnemies.push(createSupportEnemyPreview('Метатель'));
      // Возможные враги массовки: Костерез, Костяной убийца
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
      break;

    case 4:
      composition = { elite: 1, support: 3, crowd: 3 };
      // Возможные враги элиты: Каменный разрушитель, Копейщик
      possibleEnemies.push(createEliteEnemyPreview('Каменный разрушитель'));
      possibleEnemies.push(createEliteEnemyPreview('Копейщик'));
      // Возможные враги поддержки: Топор-щитовик, Мутант-кинжальщик, Костяной топорщик, Метатель
      possibleEnemies.push(createSupportEnemyPreview('Топор-щитовик'));
      possibleEnemies.push(createSupportEnemyPreview('Мутант-кинжальщик'));
      possibleEnemies.push(createSupportEnemyPreview('Костяной топорщик'));
      possibleEnemies.push(createSupportEnemyPreview('Метатель'));
      // Возможные враги массовки: Костерез, Костяной убийца
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
      break;

    case 5:
      composition = { elite: 2, support: 3, crowd: 4 };
      // Возможные враги элиты: Каменный разрушитель, Копейщик
      possibleEnemies.push(createEliteEnemyPreview('Каменный разрушитель'));
      possibleEnemies.push(createEliteEnemyPreview('Копейщик'));
      // Возможные враги поддержки: Топор-щитовик, Мутант-кинжальщик, Костяной топорщик, Метатель
      possibleEnemies.push(createSupportEnemyPreview('Топор-щитовик'));
      possibleEnemies.push(createSupportEnemyPreview('Мутант-кинжальщик'));
      possibleEnemies.push(createSupportEnemyPreview('Костяной топорщик'));
      possibleEnemies.push(createSupportEnemyPreview('Метатель'));
      // Возможные враги массовки: Костерез, Костяной убийца
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
      break;

    default:
      // Для глубин больше 5 используем логику глубины 5
      composition = { elite: 2, support: 3, crowd: 4 };
      possibleEnemies.push(createEliteEnemyPreview('Каменный разрушитель'));
      possibleEnemies.push(createEliteEnemyPreview('Копейщик'));
      possibleEnemies.push(createSupportEnemyPreview('Топор-щитовик'));
      possibleEnemies.push(createSupportEnemyPreview('Мутант-кинжальщик'));
      possibleEnemies.push(createSupportEnemyPreview('Костяной топорщик'));
      possibleEnemies.push(createSupportEnemyPreview('Метатель'));
      possibleEnemies.push(createCrowdEnemyPreview('Костерез'));
      possibleEnemies.push(createCrowdEnemyPreview('Костяной убийца'));
  }

  return {
    depth,
    enemyCount: composition.elite + composition.support + composition.crowd,
    composition,
    possibleEnemies,
  };
};

// Получаем лут для глубины
export const getDepthLoot = (depth: number): { gold: number; metal: number; wood: number; leather: number; crystals: number } => {
  switch (depth) {
    case 1:
      return { gold: 10, metal: 5, wood: 5, leather: 3, crystals: 1 };
    case 2:
      return { gold: 25, metal: 10, wood: 10, leather: 6, crystals: 2 };
    case 3:
      return { gold: 50, metal: 20, wood: 15, leather: 9, crystals: 3 };
    case 4:
      return { gold: 100, metal: 35, wood: 25, leather: 12, crystals: 4 };
    case 5:
      return { gold: 200, metal: 50, wood: 40, leather: 15, crystals: 5 };
    default:
      // Для глубин больше 5 используем логику глубины 5
      return { gold: 200, metal: 50, wood: 40, leather: 15, crystals: 5 };
  }
};
